import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, Eye } from 'lucide-react';
import { CATEGORY_COLORS, timeAgo } from './HeroSection';

function readTime(content) {
  const words = (content || '').split(/\s+/).length;
  return Math.max(1, Math.ceil(words / 200));
}

export default function StoryCard({ article, featured = false, compact = false }) {
  if (!article) return null;
  if (compact) {
    return (
      <Link to={`/article/${article.slug}`} className="flex gap-3 group p-2 rounded-xl hover:bg-white dark:hover:bg-white/5 transition-colors" data-testid="compact-story-card">
        <div className="flex-shrink-0 w-20 h-14 rounded-lg overflow-hidden bg-gray-100 dark:bg-white/5">
          {article.featured_image ? (
            <img src={article.featured_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" decoding="async" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-sky-accent/10 to-indigo-accent/10" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          {article.category && <span className="text-[10px] font-display font-semibold uppercase tracking-wider" style={{ color: CATEGORY_COLORS[article.category] || '#1a56db' }}>{article.category}</span>}
          <h4 className="font-display text-sm font-semibold text-navy dark:text-white leading-snug line-clamp-2 group-hover:text-sky-accent transition-colors">{article.title}</h4>
          <span className="text-[10px] text-gray-400 mt-0.5 block">{timeAgo(article.published_at)}</span>
        </div>
      </Link>
    );
  }
  return (
    <article className="article-card group bg-white dark:bg-navy-light rounded-2xl overflow-hidden border border-gray-100 dark:border-white/5 flex flex-col h-full" data-testid="story-card">
      <Link to={`/article/${article.slug}`} className="block flex-shrink-0">
        <div className={`overflow-hidden ${featured ? 'aspect-[16/10]' : 'aspect-[16/10]'}`}>
          {article.featured_image ? (
            <img src={article.featured_image} alt={article.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-sky-accent/10 to-indigo-accent/10 flex items-center justify-center">
              <span className="text-gray-300 font-display text-xs">CT</span>
            </div>
          )}
        </div>
      </Link>
      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-center gap-2 mb-1.5">
          {article.category && (
            <Link to={`/category/${article.category.toLowerCase()}`} className="text-[10px] font-display font-semibold uppercase tracking-wider" style={{ color: CATEGORY_COLORS[article.category] || '#1a56db' }}>
              {article.category}
            </Link>
          )}
          {article.is_breaking && (
            <span className="text-[9px] font-display font-bold uppercase tracking-wider bg-red-500 text-white px-1.5 py-0.5 rounded-full">Breaking</span>
          )}
        </div>
        <Link to={`/article/${article.slug}`} className="flex-1">
          <h3 className={`font-serif font-bold text-navy dark:text-white mb-2 leading-tight group-hover:text-sky-accent transition-colors line-clamp-2 ${featured ? 'text-lg lg:text-xl' : 'text-base'}`}>
            {article.title}
          </h3>
        </Link>
        {article.excerpt && (
          <p className={`text-gray-500 dark:text-gray-400 line-clamp-2 mb-2 ${featured ? 'text-sm' : 'text-xs'}`}>{article.excerpt}</p>
        )}
        <div className="flex items-center gap-3 text-[11px] text-gray-400 font-display mt-auto pt-2">
          <span className="flex items-center gap-1"><Clock size={10} /> {timeAgo(article.published_at)}</span>
          <span className="flex items-center gap-1"><Eye size={10} /> {readTime(article.content || article.excerpt)} min</span>
        </div>
      </div>
    </article>
  );
}
